import { Box, Button, Paper } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Stepper } from 'greener-ui-library';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ButtonWithConfirmationDialog from 'components/buttons/ButtonWithConfirmationDialog';

export interface BlueprintStepTemplateProps {
  children: ReactNode;
  currentStep: number;
  steps: string[];
  isFirstStep?: boolean;
  isLastStep?: boolean;
  onNext: () => void;
  onPrevious: () => void;
  onCancel: () => void;
  onSkipAndSave?: () => void;
  showSkipButton?: boolean;
  previousButtonLabel?: string;
  cancelButtonLabel?: string;
  submitButtonLabel?: string;
  isSubmitting?: boolean;
}

export const BlueprintStepTemplate = ({
  children,
  currentStep,
  steps,
  isFirstStep = false,
  isLastStep = false,
  onNext,
  onPrevious,
  onCancel,
  onSkipAndSave,
  showSkipButton = false,
  previousButtonLabel,
  cancelButtonLabel,
  submitButtonLabel,
  isSubmitting = false,
}: BlueprintStepTemplateProps) => {
  const { t } = useTranslation();

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        padding: '32px',
        flexDirection: 'column',
      }}
    >
      <Stepper steps={steps} activeStep={currentStep} />

      <Box sx={{ width: '100%' }} mt={5}>{children}</Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          width: '100%',
          gap: { xs: 2, sm: 0 },
        }}
        mt={2}
      >
        <Box>
          <Button
            data-testid="step-previous"
            onClick={onPrevious}
            fullWidth
            sx={{ display: isFirstStep ? 'none' : 'block' }}
          >
            {previousButtonLabel || t('blueprints:wizard.navigation.previous')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <ButtonWithConfirmationDialog
            handleConfirm={onCancel}
            title={t('blueprints:wizard.cancelDialog.title')}
            buttonText={cancelButtonLabel || t('blueprints:wizard.navigation.cancel')}
            confirmButtonText={t('blueprints:wizard.cancelDialog.confirm')}
            cancelButtonText={t('blueprints:wizard.cancelDialog.deny')}
            color="primary"
          >
            {t('blueprints:wizard.cancelDialog.message')}
          </ButtonWithConfirmationDialog>

          {showSkipButton && !isLastStep && (
            <Button
              data-testid="step-save"
              onClick={onSkipAndSave}
              variant="outlined"
              disabled={isSubmitting}
            >
              {t('blueprints:wizard.navigation.save')}
            </Button>
          )}

          <LoadingButton
            data-testid="step-next"
            onClick={onNext}
            loading={isSubmitting}
            variant="contained"
          >
            {isLastStep
              ? submitButtonLabel || t('blueprints:wizard.navigation.create')
              : t('blueprints:wizard.navigation.next')}
          </LoadingButton>
        </Box>
      </Box>
    </Paper>
  );
};
